import { FC } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, CircularProgress, Tooltip } from '@mui/material';
import { useTree } from '@/hooks/useTree';
import { useBackend } from '@/hooks/useBackend';
import { useUIStateStore } from '@/stores/uiStateStore';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { useSnackbar } from '@/hooks/useSnackbar';
import { NodeType } from '@/types/tree';
import { useNodeActions } from '@/hooks/useNodeActions';
import { TooltipProvider } from '@/components/tree/MenuTooltips';
import { TreeKeyboardShortcutsMenuFooter } from '@/components/tree/TreeKeyboardShortcutsMenuFooter';
import { iconMap } from '@/lib/theme/iconMapping';
import { useTreeStore } from '@/stores/treeStore';

/**
 * Position coordinates for the context menu
 */
interface MenuPosition {
  x: number;
  y: number;
}

/**
 * Props interface for the ContextMenu component
 */
interface ContextMenuProps {
  /** Type of the node (sample, activity, project, etc.) */
  type: NodeType;
  /** ID of the node */
  nodeId: string;
  /** Position where to show the context menu */
  position: MenuPosition | null;
  /** Callback when the menu is closed */
  onClose: () => void;
}

/**
 * ContextMenu component that uses centralized action registry
 */
const ContextMenu: FC<ContextMenuProps> = ({ type, nodeId, position, onClose }) => {
  const { getTypedData, getNodeData } = useTree();
  const nodes = useTreeStore((state) => state.nodes); // ⚠️ For context menu only

  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { setViewingNode, setSharingNode } = useUIStateStore();

  // Get the node from the store
  const node = nodes.get(nodeId);
  // Get actions for this node using the centralized registry
  const { contextMenuActions, executeNodeAction } = useNodeActions(node);

  if (!node) {
    log.error('[ContextMenu] Node not found:', nodeId);
    return null;
  }

  const isLoading = (actionId: string) => useTreeStore.getState().loading.get(actionId) || false;

  const handleMenuItemClick = async (actionId: string) => {
    log.debug('[ContextMenu] Menu item clicked:', { actionId, nodeId });

    try {
      // Special handling for actions that need UI state
      if (actionId === 'view-data') {
        setViewingNode({ type, id: nodeId });
        onClose();
        return;
      }

      if (actionId === 'share') {
        setSharingNode({ type, id: nodeId });
        onClose();
        return;
      }

      // Execute the action using the centralized registry
      await executeNodeAction(actionId, backend, onClose);
    } catch (error) {
      log.error('[ContextMenu] Error handling context menu action:', { actionId, error });
      showSnackbar(getProblemDetail(error) ?? 'Failed to perform action.', 'error');
    }
  };

  if (contextMenuActions.length === 0) {
    return null;
  }

  return (
    <Menu
      open={Boolean(position)}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position ? { top: position.y, left: position.x } : undefined}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
      sx={{
        '& .MuiPaper-root': {
          minWidth: 200,
        },
      }}
    >
      {contextMenuActions.map((action, index) => {
        const isItemLoading = isLoading(action.id);
        const tooltipText = TooltipProvider.getTooltip(action.id as any, type, true, isItemLoading);
        const isDisabled = action.isDisabled?.(node, getTypedData, getNodeData) || false;

        return (
          <div key={action.id}>
            <Tooltip title={tooltipText} placement="right" arrow>
              <MenuItem onClick={() => handleMenuItemClick(action.id)} disabled={isItemLoading || isDisabled}>
                <ListItemIcon>
                  {isItemLoading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : iconMap[action.icon]}
                </ListItemIcon>
                <ListItemText>{action.label}</ListItemText>
              </MenuItem>
            </Tooltip>
            {index < contextMenuActions.length - 1 ? <Divider /> : null}
          </div>
        );
      })}
      <TreeKeyboardShortcutsMenuFooter />
    </Menu>
  );
};

export default ContextMenu;
